import React, { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

import {
  useRoles,
  useCreateRole,
  useEditRole,
  useDeleteRole,
} from "hooks/useRolePermission"
import { useListParams } from "hooks/useListParams"
import { useFormik } from "formik"
import * as Yup from "yup"
import { formatReadableDate } from "utils/formatDate"
import { Link } from "react-router-dom"
import DisabledLink from "components/Common/DisabledLink"

const RoleList = () => {
  document.title = "Role List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [role, setRole] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: roleResponse,
    isLoading,
    refetch: roleRefetch,
    isRefetching: roleRefetchLoading,
  } = useRoles(queryParams)

  const { mutateAsync: createRole, isPending: roleCreateLoading } =
    useCreateRole()
  const { mutateAsync: editRole, isPending: roleEditLoading } = useEditRole()
  const { mutateAsync: deleteRole, isPending: roleDeleteLoading } =
    useDeleteRole()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setRole(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setRole(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleRoleClick = useCallback(
    arg => {
      setRole({ id: arg.id, name: arg.name, description: arg.description })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(roleData => {
    setRole(roleData)
    setDeleteModal(true)
  }, [])

  const handleDeleteRole = useCallback(async () => {
    if (role?.id) {
      try {
        const res = await deleteRole(role.id)
        toast.success(res?.message ?? "Role deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete role:", error)
        toast.error("Failed to delete role.")
      }
    }
  }, [role, deleteRole])

  const handleRoleListRefresh = () => {
    roleRefetch()
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters")
      .required("Please enter role name"),
    description: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = { name: values.name, description: values.description }
      const action = isEdit
        ? editRole({ id: role.id, ...payload })
        : createRole(payload)

      try {
        const res = await action
        toast.success(
          res?.message ?? `Role ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        console.error("Failed to save role:", error)
        toast.error("Failed to save role.")
      }
    },
    [isEdit, role, editRole, createRole, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: role?.name || "",
      description: role?.description || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Name", accessorKey: "name" },
      { header: "Slug", accessorKey: "slug" },
      { header: "Description", accessorKey: "description" },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const isSystem = row?.original?.is_system || false
          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <li data-bs-toggle="tooltip" data-bs-placement="top" title="View">
                <DisabledLink
                  to={`/role/${row.original.slug}/permission-list`}
                  disabled={isSystem}
                  className="btn btn-sm btn-soft-primary"
                  id={`viewtooltip-${row.original.id}`}
                >
                  <i className="mdi mdi-eye-outline" />
                </DisabledLink>

                <UncontrolledTooltip
                  placement="top"
                  target={`viewtooltip-${row.original.id}`}
                >
                  View / Assigned Permissions
                </UncontrolledTooltip>
              </li>
              <li>
                <Button
                  color="soft-info"
                  size="sm"
                  onClick={() => handleRoleClick(row.original)}
                  id={`edittooltip-${row.original.id}`}
                  disabled={isSystem}
                >
                  <i className="mdi mdi-pencil-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`edittooltip-${row.original.id}`}
                  >
                    Edit
                  </UncontrolledTooltip>
                </Button>
              </li>
              <li>
                <Button
                  color="soft-danger"
                  size="sm"
                  onClick={() => onClickDelete(row.original)}
                  id={`deletetooltip-${row.original.id}`}
                  disabled={isSystem}
                >
                  <i className="mdi mdi-delete-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`deletetooltip-${row.original.id}`}
                  >
                    Delete
                  </UncontrolledTooltip>
                </Button>
              </li>
            </ul>
          )
        },
      },
    ],
    [handleRoleClick, onClickDelete]
  )

  const rolesData = roleResponse?.data?.data || []
  const paginationInfo = roleResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteRole}
        loading={roleDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Role" breadcrumbItem="Role List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom d-flex align-items-center">
                  <h5 className="mb-0 card-title flex-grow-1">Role Lists</h5>
                  <div className="flex-shrink-0">
                    <Button
                      color="primary"
                      onClick={handleAddNewClick}
                      className="me-1"
                    >
                      Add New Role
                    </Button>
                    <Button
                      color="light"
                      onClick={handleRoleListRefresh}
                      className="me-1"
                    >
                      <i
                        className={`mdi mdi-refresh ${
                          roleRefetchLoading ? "mdi-spin" : ""
                        }`}
                      />
                    </Button>
                    {hasActiveFilters && (
                      <Button
                        color="danger"
                        onClick={resetParams}
                        className="me-1"
                      >
                        <i className="mdi mdi-close" /> Reset Filters
                      </Button>
                    )}
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={rolesData??[]}
                    isPagination
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || roleRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Role" : "Add Role"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Role Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter role name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name && (
                    <FormFeedback>{validation.errors.name}</FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    name="description"
                    id="description"
                    type="textarea"
                    placeholder="Enter description"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.description}
                  />
                </div>

                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={roleEditLoading || roleCreateLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Role"}
                    {(roleEditLoading || roleCreateLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default RoleList
